import FeatherIcon from 'feather-icons-react';
import React from 'react'
import { Button, Col, Nav, Offcanvas, Row, Tab, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import '../sass/displayUnitEdit.scss';
import InputNumberComponent from '../../../../../../../componentsV2/DataEntryComponents/InputNumber/InputNumber';
import Buttons from '../../../../../../../componentsV2/DataEntryComponents/Button/Buttons';

const ConfigurationBox = ({
    isRTL,
    dmode,
    displayUnitData,
    selectedShelf,
    depthValue,
    setDepthValue,
    sspaceValue,
    setSSpaceValue,
    ffloorValue,
    setFFloorValue,
    thicknessValue,
    setThicknessValue,
    onShelfPropertyChange,
    heightAdjustmentStep = 5,
    gapAdjustmentStep = 2,
    onAddShelfAbove,
    onAddShelfBelow,
    onDeleteShelf,
    displayRatio,
}) => {
    const {t} = useTranslation();

    const handleKeyPress = (event) => {};

    // Calculate "from the floor" value for the selected shelf
    // This calculates the sum of heights and gaps of all shelves below the selected shelf
    const calculateFromFloorValue = (selectedShelf) => {
        if (!selectedShelf || !selectedShelf.field || !selectedShelf.field.shelf) {
            return 0;
        }

        const allShelves = selectedShelf.field.shelf;
        const currentShelfRank = selectedShelf.rank;

        // Sum up the drawHeight and drawGap of all shelves below the current shelf
        let fromFloorValue = 0;

        for (let i = 0; i < allShelves.length; i++) {
            const shelf = allShelves[i];
            // Only include shelves with rank greater than current shelf (shelves below it)
            if (shelf.rank > currentShelfRank) {
                fromFloorValue += shelf.drawHeight + shelf.drawGap;
            }
        }

        return fromFloorValue / displayRatio;
    };

    // Get values from selectedShelf or use defaults
    const depth = selectedShelf?.width || 0;
    const space = selectedShelf?.height || 0;
    const fromFloor = calculateFromFloorValue(selectedShelf);
    const thickness = selectedShelf?.gap || 0;

    // if selected shelf is last shelf
    const isLastShelf = selectedShelf && selectedShelf?.rank === Object.keys(selectedShelf?.field?.shelf).length;

    const handleDepthChange = (value) => {
        if (selectedShelf && onShelfPropertyChange) {
            const success = onShelfPropertyChange('width', value);
            if (!success) {
                // Revert to original value if change failed
                setDepthValue(selectedShelf.field.depth);
            }
        }
    };

    const handleSpaceChange = (value) => {
        if (selectedShelf && onShelfPropertyChange) {
            const success = onShelfPropertyChange('height', value);
            if (!success) {
                // Revert to original value if change failed
                setSSpaceValue(selectedShelf.shelf.height);
            }
        }
    };

    const handleFromFloorChange = (value) => {
        if (selectedShelf && onShelfPropertyChange) {
            const success = onShelfPropertyChange('fromFloor', value);
            if (!success) {
                // Revert to original value if change failed
                const originalFromFloor = calculateFromFloorValue(selectedShelf);
                setFFloorValue(originalFromFloor);
            }
        }
    };

    const handleThicknessChange = (value) => { 
        if (selectedShelf && onShelfPropertyChange) {
            const success = onShelfPropertyChange('gap', value);
            if (!success) {
                // Revert to original value if change failed
                setThicknessValue(selectedShelf.shelf.gap);
            }
        }
    };

    // Show message if no shelf is selected
    if (!selectedShelf) {
        return (
            <div className="du-configuration-box">
                <p className='du-tagline text-center' style={{fontSize:12, padding: '20px'}}>
                    {t('SELECT_SHELF_TO_EDIT') || 'Please select a shelf to edit'}
                </p>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="du-configuration-box">
                
                <Buttons
                    className={`du-configuration-box-delete rounded-circle ${isRTL === "rtl" ? "du-delete-rtl" : "du-delete-ltr"}`}
                    size="sm"
                    clickEvent={onDeleteShelf}
                    variant="outline-danger"
                    dir={isRTL}
                    btnIcon={<FeatherIcon icon="trash-2" size={12} />}
                    overlay={<Tooltip id="tooltip-delete">Delete</Tooltip>}
                />

                <div className="du-configuration-box-separator-row">
                    <hr className="du-configuration-box-separator" />
                    <Buttons
                        className="du-configuration-box-center-button"
                        size="sm"
                        clickEvent={onAddShelfAbove}
                        variant="success"
                        dir={isRTL}
                        btnIcon={<FeatherIcon icon="plus" size={12} />}
                        overlay={<Tooltip id="tooltip-add-above">{t('ADD_SHELF_ABOVE') || 'Add Shelf Above'}</Tooltip>}
                    />
                    <hr className="du-configuration-box-separator" />
                </div>

                <Row>
                    <Col xs="4" className="d-flex align-items-center justify-content-between mb-2">
                        <InputNumberComponent
                            dmode={dmode}
                            value={depthValue}
                            initialValue={depthValue}
                            min={1}
                            onValueChange={(value, name) => {
                                handleDepthChange(value);
                            }}
                            placeholder={t('NUM')}
                            name="depth"
                            label={t('depth')}
                            dir={isRTL}
                            handleKeyPress={handleKeyPress}
                            allowDecimal={false}
                        />
                    </Col>
                    <Col xs="4" className="d-flex align-items-center justify-content-center mb-2">
                        <InputNumberComponent
                            dmode={dmode}
                            value={Math.round(sspaceValue)}
                            initialValue={Math.round(sspaceValue)}
                            min={1}
                            onValueChange={(value, name) => {
                                handleSpaceChange(value);
                            }}
                            placeholder={t('NUM')}
                            name="space"
                            label={t('S_SPACE')}
                            dir={isRTL}
                            handleKeyPress={handleKeyPress}
                            allowDecimal={true}
                            step={heightAdjustmentStep}
                            disabled={isLastShelf}
                        />
                    </Col>
                    <Col xs="4" className="d-flex align-items-center justify-content-end mb-2">
                        <InputNumberComponent
                            dmode={dmode}
                            value={Math.trunc(ffloorValue)}
                            initialValue={Math.trunc(ffloorValue)}
                            min={1}
                            onValueChange={(value, name) => {
                                handleFromFloorChange(value);
                            }}
                            placeholder={t('NUM')}
                            name="fromFloor"
                            label={t('FROM_FLOOR')}
                            dir={isRTL}
                            handleKeyPress={handleKeyPress}
                            allowDecimal={true}
                            step={heightAdjustmentStep}
                            disabled={isLastShelf}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col xs="4" className="d-flex align-items-center justify-content-end mb-2">
                        <InputNumberComponent
                            dmode={dmode}
                            value={Math.trunc(thicknessValue)}
                            initialValue={Math.trunc(thicknessValue)}
                            min={1}
                            onValueChange={(value, name) => {
                                handleThicknessChange(value);
                            }}
                            placeholder={t('NUM')}
                            name="thickness"
                            label={t('THICKNESS')}
                            dir={isRTL}
                            handleKeyPress={handleKeyPress}
                            allowDecimal={true}
                            step={gapAdjustmentStep}
                            disabled={isLastShelf}
                        />
                    </Col>
                </Row>
                
                <div className="du-configuration-box-separator-row">
                    <hr className="du-configuration-box-separator" />
                    <Buttons
                        className="du-configuration-box-center-button"
                        size="sm"
                        clickEvent={onAddShelfBelow}
                        variant="success"
                        dir={isRTL}
                        btnIcon={<FeatherIcon icon="plus" size={12}/>}
                        overlay={<Tooltip id="tooltip-add-below">{t('ADD_SHELF_BELOW') || 'Add Shelf Below'}</Tooltip>}
                        isDisbled={isLastShelf}
                    />
                    <hr className="du-configuration-box-separator" />
                </div>

                {/* <p>Selected Shelf Details</p>
                <p>{selectedShelf ? `Shelf ${selectedShelf.rank}` : "No shelf selected"}</p>
                <p>{selectedShelf ? `y: ${selectedShelf.shelf.y}` : ""}</p>
                <p>{selectedShelf ? `height: ${selectedShelf.shelf.height}` : ""}</p>
                <p>{selectedShelf ? `draw hight: ${selectedShelf.shelf.drawHeight}cm` : ""}</p> */}

            </div>
        </React.Fragment>
    )
}

export default ConfigurationBox;
